import {makeAutoObservable, observable, IReactionDisposer, reaction, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {RequestState, ContestStatus, ModalType} from "data/enums";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import type {IRosterStore} from "data/stores/roster/roster.store";
import type {IPlayerStatsStore} from "data/stores/player_stats/player_stats.store";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {extractErrorMessage, trackSentryErrors} from "data/utils";
import {first} from "lodash";
import {DateTime} from "luxon";
import {GAME_TIMEZONE} from "data/constants";
import {AdobeDTM} from "data/utils/AdobeDTM";

export interface ITomorrowGameController extends ViewController {
	i18n: ILocalizationStore;

	get isLoading(): boolean;

	get isWheelView(): boolean;
}

@injectable()
export class TomorrowGameController implements ITomorrowGameController {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable _reactionDisposers: IReactionDisposer[] = [];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ContestsStore) private readonly _contestStore: IContestsStore,
		@inject(Bindings.RosterStore) private readonly _rosterStore: IRosterStore,
		@inject(Bindings.PlayerStatsStore) private readonly _playerStats: IPlayerStatsStore,
		@inject(Bindings.TournamentsStore) private readonly _tournamentsStore: ITournamentsStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get contest() {
		return first(this._contestStore.scheduledContests);
	}

	get isLoading(): boolean {
		return this._requestState !== RequestState.SUCCESS;
	}

	get isWheelView(): boolean {
		const rosterCount = this._rosterStore.rosterPlayersCount;

		if (!rosterCount) {
			return true;
		}

		if (this.isContestComplete) {
			return false;
		}

		return this._rosterStore.isWheelView;
	}

	private get isContestComplete(): boolean {
		return this.contest?.status === ContestStatus.Complete;
	}

	getContestDay(contestId: number): string {
		const currentContest = this._contestStore.getContestById(contestId);
		if (!currentContest) {
			return "-";
		}

		const contestDate = DateTime.fromISO(currentContest.date).setZone(GAME_TIMEZONE);
		return contestDate.isValid ? contestDate.toFormat("LLL dd, yyyy") : "-";
	}

	init(param: void) {
		const fetchDataDisposer = reaction(() => this.contest, this.fetchData, {
			fireImmediately: true,
		});

		const tournamentsUpdate = reaction(
			() => this._tournamentsStore.activeTournaments,
			() => {
				void this._rosterStore.fetchRoster();
			}
		);
		this._reactionDisposers.push(fetchDataDisposer);
		this._reactionDisposers.push(tournamentsUpdate);
	}

	dispose() {
		this._reactionDisposers.forEach((dispose) => dispose());
		this._contestStore.toContestId(this._contestStore.currentGameContest?.id || 0);
	}

	private fetchData = () => {
		const contest = this.contest;
		this._requestState = RequestState.PENDING;
		if (contest) {
			this._rosterStore.updateContestId(contest.id);
			void Promise.all([
				this._rosterStore.fetchRoster(),
				this._rosterStore.fetchPlayedContests(),
				this._playerStats.fetchStats(),
			])
				.then(() => {
					runInAction(() => {
						this._requestState = RequestState.SUCCESS;
					});

					AdobeDTM.trackState({
						"page.legacyName": "Major League Baseball: Spinball: My Team",
						"page.state": "Spin for Tomorrow",
						"baseball.matchupDate": this.getContestDay(contest.id),
					});
				})
				.catch((e) => {
					runInAction(() => {
						this._requestState = RequestState.ERROR;
					});
					this._modalsStore.showModal(ModalType.ERROR, {
						message: extractErrorMessage(e),
					});
					trackSentryErrors(e, {}, "wheel.fetchData");
				});
		}
	};
}
